import {Cell, Row, type Workbook, Worksheet} from "exceljs";
import {useState} from "react";
import {FormHandler} from "@/components/address-book/Form";
import {
    CorruptedExcelFileError,
    MAXIMUM_ADDRESS_BOOK_ENTRIES,
    NoSheetInExcelFileError,
    removeExcelEmailLink,
    TextFormat,
    TooManyAddressBookEntriesError,
} from "@/utils/excel";
import {defineMessage, IntlShape} from "react-intl";
import {useAddressBookListQuery} from "./queries.generated";
import {AddressBookEntryData} from "@/api";
import {getRandomString} from "@/tools";

// Columns for the excel file
const WorkbookLayout = {
    headers: [
        {
            key: "sn",
            width: 20,
            style: TextFormat,
            label: defineMessage({id: "address_book.sn", defaultMessage: "Surname"}),
        },
        {
            key: "givenName",
            width: 20,
            style: TextFormat,
            label: defineMessage({id: "address_book.givenName", defaultMessage: "Given name"}),
        },
        {
            key: "telephoneNumber",
            width: 40,
            style: TextFormat,
            label: defineMessage({id: "address_book.telephoneNumber", defaultMessage: "Telephone number"}),
        },
        {
            key: "mail",
            width: 30,
            style: TextFormat,
            label: defineMessage({id: "address_book.mail", defaultMessage: "Email"}),
        },
        {
            key: "departmentNumber",
            width: 25,
            style: TextFormat,
            label: defineMessage({id: "address_book.departmentNumber", defaultMessage: "Department number"}),
        },
        {
            key: "o",
            width: 25,
            style: TextFormat,
            label: defineMessage({id: "address_book.o", defaultMessage: "Company"}),
        },
        {
            key: "mobile",
            width: 30,
            style: TextFormat,
            label: defineMessage({id: "address_book.mobile", defaultMessage: "Mobile number"}),
        },
        {
            key: "homePhone",
            width: 30,
            style: TextFormat,
            label: defineMessage({id: "address_book.homePhone", defaultMessage: "Home number"}),
        },
        {
            key: "description",
            width: 30,
            style: TextFormat,
            label: defineMessage({id: "address_book.description", defaultMessage: "Description"}),
        },
    ] as const,
} as const;

export const useAddressBookCRUD = (target: string) => {
    const {data, refetch} = useAddressBookListQuery({
        variables: {target},
    });

    const [formHandler, setFormHandler] = useState<FormHandler>();
    const [deleteEntryName, setDeleteEntryName] = useState<string>();

    return {data, refetch, formHandler, setFormHandler, deleteEntryName, setDeleteEntryName};
};

export const convertEntriesToExcel = async (entries: AddressBookEntryData[], intl: IntlShape): Promise<Blob> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const workbook = new Workbook();
    const externalSheet = workbook.addWorksheet(
        intl.formatMessage({id: "address_book.excel.sheets.external", defaultMessage: "External entries"}),
    );
    const internalSheet = workbook.addWorksheet(
        intl.formatMessage({id: "address_book.excel.sheets.internal", defaultMessage: "Internal entries"}),
    );

    externalSheet.columns = WorkbookLayout.headers.map((header) => ({
        header: intl.formatMessage(header.label),
        ...header,
    }));
    internalSheet.columns = WorkbookLayout.headers.map((header) => ({
        header: intl.formatMessage(header.label),
        ...header,
    }));

    for (const entry of entries) {
        const rowData = {
            sn: entry.sn,
            givenName: entry.givenName,
            telephoneNumber: entry.telephoneNumber.join(", "),
            mail: entry.mail,
            departmentNumber: entry.departmentNumber,
            o: entry.o,
            mobile: entry.mobile,
            homePhone: entry.homePhone,
            description: entry.description,
        };

        if (entry.uid) {
            internalSheet.addRow(rowData);
        } else {
            externalSheet.addRow(rowData);
        }
    }

    internalSheet.protect(getRandomString(20), {
        insertRows: false,
        deleteRows: false,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
};

const getTelephoneNumbers = (cellString: string | null): string[] => {
    if (cellString == null) return null;
    return cellString.split(",").map((number) => number.trim());
};

const removeLink = (cell: Cell): string | null => {
    const trimmed = removeExcelEmailLink(cell.value);
    if (trimmed === "") return null;
    return trimmed;
};

/**
 * Extracts the entries from an excel file
 * @param file The excel file
 * @returns The entries
 * @throws {CorruptedExcelFileError} If the excel file is corrupted
 * @throws {NoSheetInExcelFileError} If the excel file has no sheet
 * @throws {TooManyAddressBookEntriesError} If the excel file has too many entries
 */
export const getEntriesFromExcel = async (file: File): Promise<AddressBookEntryData[]> => {
    const {Workbook} = await import(/* webpackChunkName: "exceljs" */ "exceljs");
    const loader = new Workbook();
    let workbook: Workbook;
    try {
        workbook = await loader.xlsx.load(await file.arrayBuffer());
    } catch {
        throw new CorruptedExcelFileError();
    }

    let sheet: Worksheet;
    try {
        sheet = workbook.getWorksheet(1);
    } catch {
        throw new NoSheetInExcelFileError();
    }

    const entries: AddressBookEntryData[] = [];
    sheet.eachRow((row: Row, rowNumber: number) => {
        if (rowNumber === 1) return; // skip header row
        entries.push({
            sn: row.getCell(1).value?.toString(),
            givenName: row.getCell(2).value?.toString(),
            telephoneNumber: getTelephoneNumbers(row.getCell(3).value?.toString()),
            mail: removeLink(row.getCell(4)),
            departmentNumber: row.getCell(5).value?.toString(),
            o: removeLink(row.getCell(6)),
            mobile: row.getCell(7).value?.toString(),
            homePhone: row.getCell(8).value?.toString(),
            description: row.getCell(9).value?.toString(),
        });
    });

    if (entries.length > MAXIMUM_ADDRESS_BOOK_ENTRIES) {
        throw new TooManyAddressBookEntriesError();
    }

    return entries;
};
